import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Box, SimpleGrid, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { TbReportMoney } from 'react-icons/tb';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import type { IData, IPagination, ISelect } from '../../../../types/shared';
import { useCompanies } from '../../../../hooks/useCompanies';
import type { ICompany, ITaxRegime } from '../../../../hooks/useCompanies';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { TaxRegimeSummaryCards } from './components/TaxRegimeSummaryCards';
import { TaxRegimeDetailModal } from './components/TaxRegimeDetailModal';
import { TaxRegimeTable } from './components/TaxRegimeTable';
import { TaxRegimeCard } from './components/TaxRegimeCard';
import { TaxRegimeFilters } from './components/TaxRegimeFilters';

// Função para determinar o modo de busca baseado nos filtros da URL
const getSearchModeFromFilters = (filters: URLSearchParams): 'ID' | 'Nome' | 'CNPJ' => {
  if (filters.get('external_id')) return 'ID';
  if (filters.get('cnpj')) return 'CNPJ';
  return 'Nome';
};

// Função para obter o valor de busca atual baseado no modo
const getSearchValueFromFilters = (filters: URLSearchParams): string => {
  return filters.get('external_id') || filters.get('cnpj') || filters.get('search') || '';
};

export function ModulesHomeSupervisorTaxRegimePage() {
  const { getTaxRegime, getQuebrasTaxRegimeTotais } = useCompanies();
  const {
    isOpen: isDetailModalOpen,
    onOpen: onDetailModalOpen,
    onClose: onDetailModalClose,
  } = useDisclosure();
  const [selectedCompany, setSelectedCompany] = useState<ITaxRegime | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [allTaxRegimes, setAllTaxRegimes] = useState<ITaxRegime[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const previousFilterKeyRef = useRef<string>('');

  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      page: '1',
      size: '50',
      search: '',
      date_start: moment().startOf('year').format('YYYY-MM-DD'),
      date_end: moment().endOf('year').format('YYYY-MM-DD'),
    }),
  );

  // Inferir searchMode da URL ao carregar e quando filtros mudam
  const [searchMode, setSearchMode] = useState<'ID' | 'Nome' | 'CNPJ'>(() =>
    getSearchModeFromFilters(filters),
  );

  // Atualizar searchMode quando a URL muda (ex: navegação direta)
  useEffect(() => {
    const modeFromUrl = getSearchModeFromFilters(filters);
    if (modeFromUrl !== searchMode) {
      // Só atualiza se há um filtro de busca ativo
      if (filters.get('external_id') || filters.get('cnpj') || filters.get('search')) {
        setSearchMode(modeFromUrl);
      }
    }
  }, [filters, searchMode]);

  // Calcular filtros ativos para indicadores visuais
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.get('external_id')) count += 1;
    if (filters.get('cidade')) count += 1;
    if (filters.get('uf')) count += 1;
    if (filters.get('grupo_de_empresas')) count += 1;
    if (filters.get('tags')) count += 1;
    if (filters.get('comercial')) count += 1;
    if (filters.get('contabil')) count += 1;
    if (filters.get('financeiro')) count += 1;
    if (filters.get('contas_a_pagar')) count += 1;
    if (filters.get('encerramento_anual')) count += 1;
    if (filters.get('fiscal')) count += 1;
    if (filters.get('imposto_de_renda')) count += 1;
    if (filters.get('integracao_contabil')) count += 1;
    if (filters.get('integracao_fiscal')) count += 1;
    if (filters.get('pessoal')) count += 1;
    if (filters.get('relacionamento_com_cliente')) count += 1;
    if (filters.get('societario')) count += 1;
    return count;
  }, [filters]);

  // Função para aplicar os filtros avançados quando clica no botão "Filtrar"
  const handleApplyFilters = (values: {
    companies?: ICompany;
    cidade?: ISelect<string>;
    uf?: ISelect<string>;
    grupo_de_empresas?: ISelect<string>;
    tags?: ISelect<string>;
    comercial?: ISelect<string>;
    contabil?: ISelect<string>;
    financeiro?: ISelect<string>;
    contas_a_pagar?: ISelect<string>;
    encerramento_anual?: ISelect<string>;
    fiscal?: ISelect<string>;
    imposto_de_renda?: ISelect<string>;
    integracao_contabil?: ISelect<string>;
    integracao_fiscal?: ISelect<string>;
    pessoal?: ISelect<string>;
    relacionamento_com_cliente?: ISelect<string>;
    societario?: ISelect<string>;
  }) => {
    const newFilters = new URLSearchParams();

    // Mantém filtros básicos
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    // Mantém o regime_tributario se existir, caso contrário não define (null)
    const regime = filters.get('regime_tributario');
    if (regime) {
      newFilters.set('regime_tributario', regime);
    }
    newFilters.set(
      'date_start',
      filters.get('date_start') || moment().startOf('year').format('YYYY-MM-DD'),
    );
    newFilters.set(
      'date_end',
      filters.get('date_end') || moment().endOf('year').format('YYYY-MM-DD'),
    );

    // Preserva filtros de busca existentes (search, external_id, cnpj)
    if (filters.get('search')) newFilters.set('search', filters.get('search')!);
    if (filters.get('external_id')) newFilters.set('external_id', filters.get('external_id')!);
    if (filters.get('cnpj')) newFilters.set('cnpj', filters.get('cnpj')!);

    // Aplica filtros avançados
    if (values.companies?.external_id) {
      newFilters.set('external_id', values.companies.external_id);
    }
    if (values.cidade?.value) newFilters.set('cidade', values.cidade.value);
    if (values.uf?.value) newFilters.set('uf', values.uf.value);
    if (values.grupo_de_empresas?.value)
      newFilters.set('grupo_de_empresas', values.grupo_de_empresas.value);
    if (values.tags?.value) newFilters.set('tags', values.tags.value);
    if (values.comercial?.value) newFilters.set('comercial', values.comercial.value);
    if (values.contabil?.value) newFilters.set('contabil', values.contabil.value);
    if (values.financeiro?.value) newFilters.set('financeiro', values.financeiro.value);
    if (values.contas_a_pagar?.value) newFilters.set('contas_a_pagar', values.contas_a_pagar.value);
    if (values.encerramento_anual?.value)
      newFilters.set('encerramento_anual', values.encerramento_anual.value);
    if (values.fiscal?.value) newFilters.set('fiscal', values.fiscal.value);
    if (values.imposto_de_renda?.value)
      newFilters.set('imposto_de_renda', values.imposto_de_renda.value);
    if (values.integracao_contabil?.value)
      newFilters.set('integracao_contabil', values.integracao_contabil.value);
    if (values.integracao_fiscal?.value)
      newFilters.set('integracao_fiscal', values.integracao_fiscal.value);
    if (values.pessoal?.value) newFilters.set('pessoal', values.pessoal.value);
    if (values.relacionamento_com_cliente?.value)
      newFilters.set('relacionamento_com_cliente', values.relacionamento_com_cliente.value);
    if (values.societario?.value) newFilters.set('societario', values.societario.value);

    setFilters(newFilters);
  };

  const handleSearchChange = (search: string, mode: 'ID' | 'Nome' | 'CNPJ') => {
    const newFilters = new URLSearchParams(filters);
    // Limpa todos os filtros de busca antes de aplicar o novo
    newFilters.delete('search');
    newFilters.delete('cnpj');
    newFilters.delete('external_id');

    if (search) {
      switch (mode) {
        case 'ID':
          newFilters.set('external_id', search);
          break;
        case 'Nome':
          newFilters.set('search', search);
          break;
        case 'CNPJ':
          newFilters.set('cnpj', search.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
          break;
        default:
          break;
      }
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  };

  const handleSelectRegime = (regime: 'lucro_presumido' | 'simples_nacional') => {
    const newFilters = new URLSearchParams(Object.fromEntries(filters));
    newFilters.set('page', '1');
    // Se clicar no mesmo regime que está selecionado, remove o filtro (desseleciona)
    const currentRegime = filters.get('regime_tributario') || null;
    if (currentRegime === regime) {
      newFilters.delete('regime_tributario');
    } else {
      newFilters.set('regime_tributario', regime);
    }
    setFilters(newFilters);
  };

  const handleShowAllFaturadas = () => {
    const newFilters = new URLSearchParams(Object.fromEntries(filters));
    newFilters.set('page', '1');
    // Se já está sem filtro de regime, adiciona um marcador especial para indicar que foi clicado
    const currentRegime = filters.get('regime_tributario');
    if (currentRegime === null || currentRegime === '') {
      // Se não tem filtro, adiciona um marcador especial para mostrar apenas empresas com faturamento
      newFilters.set('regime_tributario', 'todas_faturadas');
    } else if (currentRegime === 'todas_faturadas') {
      // Se já está em "todas_faturadas", remove para deselecionar
      newFilters.delete('regime_tributario');
    } else {
      // Se tem outro filtro, muda para "todas_faturadas"
      newFilters.set('regime_tributario', 'todas_faturadas');
    }
    setFilters(newFilters);
  };

  const handleShowSemFaturamento = () => {
    const newFilters = new URLSearchParams(Object.fromEntries(filters));
    newFilters.set('page', '1');
    const currentRegime = filters.get('regime_tributario');
    if (currentRegime === 'sem_faturamento') {
      // Se já está em "sem_faturamento", remove para deselecionar
      newFilters.delete('regime_tributario');
    } else {
      // Muda para "sem_faturamento"
      newFilters.set('regime_tributario', 'sem_faturamento');
    }
    setFilters(newFilters);
  };

  const handleResetToDefault = () => {
    const newFilters = new URLSearchParams(Object.fromEntries(filters));
    newFilters.set('page', '1');
    // Remove o filtro de regime para mostrar todas as empresas (com e sem faturamento)
    newFilters.delete('regime_tributario');
    setFilters(newFilters);
  };

  const handleRowClick = (company: ITaxRegime) => {
    setSelectedCompany(company);
    onDetailModalOpen();
  };

  // Função para limpar todos os filtros avançados (mantém regime e ano)
  const handleClearFilters = () => {
    const baseParams: Record<string, string> = {
      page: '1',
      size: '50',
      date_start: filters.get('date_start') || moment().startOf('year').format('YYYY-MM-DD'),
      date_end: filters.get('date_end') || moment().endOf('year').format('YYYY-MM-DD'),
    };
    const regime = filters.get('regime_tributario');
    if (regime) {
      baseParams.regime_tributario = regime;
    }
    const clearedFilters = createSearchParams(baseParams);

    // Nota: Limpa TODOS os filtros incluindo busca (search, external_id, cnpj)
    setFilters(clearedFilters);
  };

  // Obter o ano atual do filtro
  const currentFilterYear = moment(
    filters.get('date_start') ?? moment().format('YYYY-MM-DD'),
  ).year();

  // Funções para navegar entre anos
  const handlePrevYear = () => {
    const prevYear = currentFilterYear - 1;
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: moment().year(prevYear).startOf('year').format('YYYY-MM-DD'),
        date_end: moment().year(prevYear).endOf('year').format('YYYY-MM-DD'),
        page: '1',
      }),
    );
  };

  const handleNextYear = () => {
    const nextYear = currentFilterYear + 1;
    if (nextYear <= moment().year()) {
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: moment().year(nextYear).startOf('year').format('YYYY-MM-DD'),
          date_end: moment().year(nextYear).endOf('year').format('YYYY-MM-DD'),
          page: '1',
        }),
      );
    }
  };

  const handleSelectYear = (year: number) => {
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: moment().year(year).startOf('year').format('YYYY-MM-DD'),
        date_end: moment().year(year).endOf('year').format('YYYY-MM-DD'),
        page: '1',
      }),
    );
  };

  // Filtros para o regime atual (pode ser null se nenhum estiver selecionado)
  // "todas_faturadas" e "sem_faturamento" são valores especiais
  const currentRegimeParam = filters.get('regime_tributario');
  const currentRegime =
    currentRegimeParam === 'todas_faturadas' || currentRegimeParam === 'sem_faturamento'
      ? null
      : currentRegimeParam || null;
  const isTodasFaturadasSelected = currentRegimeParam === 'todas_faturadas';
  const isSemFaturamentoSelected = currentRegimeParam === 'sem_faturamento';

  // Buscar totais de empresas usando endpoint de totais (mais eficiente)
  // Este endpoint já retorna ambos os totais (simples_nacional e lucro_presumido) em uma única requisição
  const filtersForTotals = useMemo(() => {
    const baseFilters = Object.fromEntries([...Array.from(filters)]);
    // Remove campos que não são necessários para o endpoint de totais
    delete baseFilters.page;
    delete baseFilters.size;
    delete baseFilters.regime_tributario; // Buscar ambos os regimes
    return baseFilters;
  }, [filters]);

  const { data: dataTotals, isFetching: isFetchingTotals } = useQuery({
    queryKey: ['taxRegimeTotals', filtersForTotals],
    queryFn: getQuebrasTaxRegimeTotais,
    enabled: true,
  });

  // Total de empresas geral (sem filtrar por faturamento)
  const totalEmpresas = dataTotals?.total_empresas_geral || 0;

  // Criar filtros para a query principal
  const filtersForMainQuery = useMemo(() => {
    const baseFilters = Object.fromEntries([...Array.from(filters)]);
    return cleanFilters({
      ...baseFilters,
      page: currentPage,
      size: '50',
    });
  }, [filters, currentPage]);

  const { data: dataTaxRegimeList, isFetching: isFetchingTaxRegimeList } = useQuery<
    IData<ITaxRegime[]>
  >({
    queryKey: ['taxRegimeList-taxRegime', filtersForMainQuery],
    queryFn: getTaxRegime,
  });

  const { items: taxRegimeList, ...pagination } = dataTaxRegimeList?.data ?? {
    items: [],
    ...({} as IPagination),
  };

  // Chave dos filtros para detectar mudanças (exceto page)
  const filterKey = useMemo(() => {
    const baseFilters = Object.fromEntries([...Array.from(filters)]);
    delete baseFilters.page;
    return JSON.stringify(baseFilters);
  }, [filters]);

  // Resetar currentPage quando filtros mudam (exceto page)
  useEffect(() => {
    if (previousFilterKeyRef.current && previousFilterKeyRef.current !== filterKey) {
      setCurrentPage(1);
      setHasMore(true);
    }
    previousFilterKeyRef.current = filterKey;
  }, [filterKey]);

  // Acumular dados quando uma nova página é carregada, evitando duplicados
  useEffect(() => {
    // Não atualizar enquanto está carregando para evitar estados intermediários
    if (isFetchingTaxRegimeList) {
      return;
    }

    if (taxRegimeList && taxRegimeList.length > 0) {
      if (currentPage === 1) {
        // Resetar quando é a primeira página (filtros mudaram)
        setAllTaxRegimes(taxRegimeList);
      } else {
        // Acumular quando é uma página subsequente, evitando duplicados
        setAllTaxRegimes(prev => {
          const existingIds = new Set(prev.map(item => item.external_id));
          const newItems = taxRegimeList.filter(item => !existingIds.has(item.external_id));
          return [...prev, ...newItems];
        });
      }
      // Verificar se há mais páginas
      const totalPages = Math.ceil((pagination.total || 0) / 50);
      setHasMore(currentPage < totalPages);
    } else if (taxRegimeList && taxRegimeList.length === 0 && currentPage === 1) {
      // Resetar se não há dados na primeira página
      setAllTaxRegimes([]);
      setHasMore(false);
    }
  }, [taxRegimeList, currentPage, pagination.total, isFetchingTaxRegimeList]);

  // Callback para carregar mais páginas
  const loadMore = useCallback(() => {
    if (hasMore && !isFetchingTaxRegimeList) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasMore, isFetchingTaxRegimeList]);

  // Intersection Observer para infinite scroll (funciona em ambos os modos)
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || isFetchingTaxRegimeList) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isFetchingTaxRegimeList, loadMore]);

  // Valor atual da busca para passar ao componente de filtros
  const currentSearchValue = getSearchValueFromFilters(filters);

  return (
    <div className="flex w-full flex-col gap-4 p-4">
      {/* Header com título */}
      <div className="flex items-center gap-3">
        <ReturnButton previousPage="modulesHome/supervisor" />
        <div className="flex items-center justify-center rounded-full bg-[#FFE1C6] p-3">
          <TbReportMoney color="#F2994A" className="size-8" />
        </div>
        <div className="flex flex-col justify-center gap-1">
          <Text fontWeight="bold" fontSize="16px">
            SUB.LIMITE SIMPLES E PRESUMIDO
          </Text>
          <Text fontSize="12px" color="gray.500">
            Análise de regime tributário das empresas
          </Text>
        </div>
      </div>

      {/* Cards de Status */}
      <TaxRegimeSummaryCards
        totalLucroPresumido={dataTotals?.lucro_presumido ?? 0}
        totalSimplesNacional={dataTotals?.simples_nacional ?? 0}
        totalEmpresas={totalEmpresas}
        totalSimplesGeral={dataTotals?.simples_nacional_geral ?? 0}
        totalPresumidoGeral={dataTotals?.lucro_presumido_geral ?? 0}
        totalSemFaturamento={dataTotals?.total_sem_faturamento ?? 0}
        simplesSemFaturamento={dataTotals?.simples_sem_faturamento ?? 0}
        lucroSemFaturamento={dataTotals?.lucro_sem_faturamento ?? 0}
        currentRegime={currentRegime}
        isTodasFaturadasSelected={isTodasFaturadasSelected}
        isSemFaturamentoSelected={isSemFaturamentoSelected}
        isLoading={isFetchingTaxRegimeList}
        isLoadingAlternative={isFetchingTotals}
        alertasLimiteExcedido={dataTotals?.alertas_limite_excedido ?? 0}
        alertasLimiteProximo={dataTotals?.alertas_limite_proximo ?? 0}
        alertasFaturamentoBaixo={dataTotals?.alertas_faturamento_baixo ?? 0}
        onSelectRegime={handleSelectRegime}
        onShowAllFaturadas={handleShowAllFaturadas}
        onShowSemFaturamento={handleShowSemFaturamento}
        onResetToDefault={handleResetToDefault}
      />

      {/* Filtros */}
      <TaxRegimeFilters
        filters={filters}
        currentYear={currentFilterYear}
        activeFiltersCount={activeFiltersCount}
        searchMode={searchMode}
        searchValue={currentSearchValue}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onPrevYear={handlePrevYear}
        onNextYear={handleNextYear}
        onSelectYear={handleSelectYear}
        onSearchModeChange={setSearchMode}
        onSearchChange={handleSearchChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      {/* Lista de empresas */}
      {viewMode === 'table' && (
        <Box>
          <TaxRegimeTable
            taxRegimeList={allTaxRegimes}
            isLoading={isFetchingTaxRegimeList && allTaxRegimes.length === 0}
            currentRegime={currentRegime}
            onRowClick={handleRowClick}
          />
          {/* Elemento para trigger do infinite scroll no modo tabela */}
          {hasMore && (
            <Box ref={loadMoreRef} py={4} display="flex" justifyContent="center">
              {isFetchingTaxRegimeList && <Spinner size="lg" color="blue.500" />}
            </Box>
          )}
        </Box>
      )}

      {viewMode === 'grid' && isFetchingTaxRegimeList && allTaxRegimes.length === 0 && (
        <div className="flex flex-col items-center gap-4 py-8">
          <Spinner size="lg" color="primary" />
          <span className="text-xl font-semibold">Carregando...</span>
        </div>
      )}

      {viewMode === 'grid' && allTaxRegimes?.length === 0 && !isFetchingTaxRegimeList && (
        <div className="flex flex-col items-center gap-4 py-8">Nenhum item encontrado</div>
      )}

      {viewMode === 'grid' && allTaxRegimes?.length > 0 && (
        <Box>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {allTaxRegimes?.map(item => (
              <TaxRegimeCard
                key={item.external_id}
                company={item}
                onClick={() => handleRowClick(item)}
              />
            ))}
          </SimpleGrid>

          {/* Elemento para trigger do infinite scroll */}
          {hasMore && (
            <Box ref={loadMoreRef} py={4} display="flex" justifyContent="center">
              {isFetchingTaxRegimeList && <Spinner size="lg" color="blue.500" />}
            </Box>
          )}
        </Box>
      )}

      {/* Modal de Detalhes com Gráficos */}
      <TaxRegimeDetailModal
        isOpen={isDetailModalOpen}
        onClose={onDetailModalClose}
        company={selectedCompany}
        currentYear={currentFilterYear}
      />
    </div>
  );
}
